import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { CheckCircle2, Loader2, ArrowRight, PartyPopper, Minus, Plus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iso = (d) => format(d, "yyyy-MM-dd");

const EVENT_TYPES = ["Aniversário", "Celebração familiar", "Batizado ou comunhão", "Jantar privado", "Evento empresarial", "Outro tipo de evento"];
const COOK = ["Com cozinheiro", "Sem cozinheiro", "Cozinheiro e ajudante"];
const CATERING = ["Organizado por mim", "Catering Montebello", "Menu personalizado mediante proposta"];
const DECOR = ["Sem decoração adicional", "Decoração base e montagem de mesas", "Decoração personalizada"];
const CLEANING = ["Com limpeza final", "Sem limpeza final"];
const ORGANIZATION = ["Organização por minha conta", "Quero apoio na organização"];
const EXTRAS = ["Empregados de mesa", "Mesas e cadeiras", "Loiça, copos e talheres", "Bar e bebidas", "Música ou DJ", "Decoração floral", "Fotografia e vídeo", "Montagem e desmontagem"];

const EVENT_PHOTOS = [
  {
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_montebello-lodge/artifacts/e0d15b80fa34ad67_926207998.jpg",
    alt: "Cozinha profissional em inox e pedra",
  },
  {
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_montebello-lodge/artifacts/28654b4fe3bd23b3_926208002.jpg",
    alt: "Terraço coberto com mesas para eventos",
  },
  {
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_montebello-lodge/artifacts/9222325ae2672b98_926208007.jpg",
    alt: "Zona de churrasqueira em tijolo no jardim",
  },
];

export default function GastroBookingWidget() {
  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const [date, setDate] = useState(undefined);
  const [party, setParty] = useState(20);
  const [eventType, setEventType] = useState("");
  const [cook, setCook] = useState("");
  const [catering, setCatering] = useState("");
  const [decor, setDecor] = useState("");
  const [cleaning, setCleaning] = useState("");
  const [organization, setOrganization] = useState("");
  const [extras, setExtras] = useState([]);
  const [form, setForm] = useState({ guest_name: "", guest_email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const toggleExtra = (x) => setExtras((cur) => cur.includes(x) ? cur.filter((v) => v !== x) : [...cur, x]);

  const submit = async (e) => {
    e.preventDefault();
    if (!date) return toast.error("Selecione a data pretendida.");
    if (!eventType) return toast.error("Escolha o tipo de evento.");
    if (!form.guest_name.trim() || !form.guest_email.trim()) return toast.error("Preencha o seu nome e email.");
    setLoading(true);
    try {
      const payload = {
        guest_name: form.guest_name,
        guest_email: form.guest_email,
        phone: form.phone,
        message: form.message,
        party_size: Number(party),
        date: iso(date),
        event_type: eventType,
        cook_service: cook || null,
        catering: catering || null,
        decoration: decor || null,
        cleaning: cleaning || null,
        organization: organization || null,
        extras,
      };
      const { data } = await axios.post(`${API}/gastro-bookings`, payload);
      setDone(data);
      toast.success("Pedido enviado! Entraremos em contacto para confirmar.");
      setDate(undefined); setParty(20); setEventType(""); setCook(""); setCatering("");
      setDecor(""); setCleaning(""); setOrganization(""); setExtras([]);
      setForm({ guest_name: "", guest_email: "", phone: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Não foi possível enviar o pedido.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div data-testid="gastro-success" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-terracotta text-cream p-8 lg:p-12 rounded-sm">
        <CheckCircle2 size={44} strokeWidth={1.25} className="text-cream/90" />
        <h3 className="font-display text-3xl lg:text-4xl mt-6">Pedido recebido.</h3>
        <p className="mt-4 text-cream/90 leading-relaxed max-w-md">
          Obrigado, {done.guest_name}. Recebemos o seu pedido para <strong>{done.event_type}</strong> no dia{" "}
          <strong>{format(new Date(done.date + "T00:00:00"), "d 'de' MMMM yyyy", { locale: pt })}</strong>, para cerca de {done.party_size} pessoas. Voltaremos ao seu contacto com uma proposta.
        </p>
        <button data-testid="gastro-new-btn" onClick={() => setDone(null)} className="mt-8 inline-flex items-center gap-2 h-12 px-6 rounded-full border border-cream/50 text-cream text-[0.78rem] tracking-[0.2em] uppercase hover:bg-cream/10 transition-colors">
          Fazer outro pedido
        </button>
      </motion.div>
    );
  }

  return (
    <div data-testid="gastro-widget" className="bg-white border border-border rounded-sm overflow-hidden">
      {/* Fotos do espaço */}
      <div className="grid grid-cols-3 gap-2 p-2" data-testid="gastro-event-photos">
        {EVENT_PHOTOS.map((p, i) => (
          <div key={p.src} className="overflow-hidden rounded-sm">
            <img
              src={p.src}
              alt={p.alt}
              loading="lazy"
              data-testid={`gastro-photo-${i}`}
              className="w-full h-24 sm:h-32 lg:h-36 object-cover hover:scale-105 transition-transform duration-500"
            />
          </div>
        ))}
      </div>
      <form onSubmit={submit} data-testid="gastro-form" className="p-6 lg:p-10">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14">
          {/* LEFT COLUMN */}
          <div className="space-y-8">
            {/* Date */}
            <section>
              <SectionTitle icon={PartyPopper}>Data pretendida</SectionTitle>
              <div className="border border-border rounded-sm p-4">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={{ before: today }}
                  numberOfMonths={1}
                  locale={pt}
                  fromDate={today}
                  data-testid="gastro-calendar"
                  className="p-0"
                  classNames={{
                    day_selected: "bg-terracotta text-cream hover:bg-terracotta hover:text-cream",
                    day_today: "text-terracotta font-semibold",
                    day: "h-9 w-9 p-0 font-normal rounded-md hover:bg-[#F1E7E2]",
                    caption_label: "text-sm font-medium capitalize text-forest font-display text-lg",
                    head_cell: "text-muted-foreground w-9 font-normal text-[0.7rem] uppercase tracking-wider",
                  }}
                />
              </div>
            </section>

            {/* Event type */}
            <ChoiceGroup label="Tipo de evento *" value={eventType} onChange={setEventType} options={EVENT_TYPES} testIdPrefix="event-type" />

            {/* Party size */}
            <section>
              <Label>Número estimado de pessoas</Label>
              <div className="flex items-center gap-4">
                <button type="button" data-testid="gastro-party-minus" onClick={() => setParty((p) => Math.max(1, p - 5))} className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:border-forest transition-colors"><Minus size={16} /></button>
                <span data-testid="gastro-party-value" className="font-display text-3xl text-forest w-16 text-center">{party}</span>
                <button type="button" data-testid="gastro-party-plus" onClick={() => setParty((p) => Math.min(500, p + 5))} className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:border-forest transition-colors"><Plus size={16} /></button>
                <span className="text-xs text-muted-foreground">(ajuste em passos de 5)</span>
              </div>
            </section>

            {/* Cook */}
            <ChoiceGroup label="Serviço de cozinheiro" value={cook} onChange={setCook} options={COOK} testIdPrefix="cook" />

            {/* Catering */}
            <ChoiceGroup label="Alimentação" value={catering} onChange={setCatering} options={CATERING} testIdPrefix="catering" />
          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-8">
            {/* Decoration */}
            <ChoiceGroup label="Decoração e mesas" value={decor} onChange={setDecor} options={DECOR} testIdPrefix="decor" />

            {/* Cleaning */}
            <ChoiceGroup label="Limpeza" value={cleaning} onChange={setCleaning} options={CLEANING} testIdPrefix="cleaning" />

            {/* Organization */}
            <ChoiceGroup label="Organização do evento" value={organization} onChange={setOrganization} options={ORGANIZATION} testIdPrefix="organization" />

            {/* Extras */}
            <section>
              <Label>Outros serviços pretendidos</Label>
              <div className="flex flex-wrap gap-2" data-testid="extras-group">
                {EXTRAS.map((x) => {
                  const on = extras.includes(x);
                  return (
                    <button
                      type="button"
                      key={x}
                      data-testid={`extra-${x}`}
                      onClick={() => toggleExtra(x)}
                      className={`px-4 h-9 rounded-full text-sm border transition-colors ${on ? "bg-forest text-cream border-forest" : "bg-transparent text-forest border-border hover:border-forest"}`}
                    >
                      {x}
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Contact */}
            <section className="space-y-5">
              <Label>Contactos</Label>
              <Field label="Nome *"><input data-testid="gastro-name-input" name="guest_name" value={form.guest_name} onChange={onChange} required className="input-line" placeholder="O seu nome" /></Field>
              <div className="grid grid-cols-2 gap-5">
                <Field label="Email *"><input data-testid="gastro-email-input" type="email" name="guest_email" value={form.guest_email} onChange={onChange} required className="input-line" placeholder="voce@email.com" /></Field>
                <Field label="Telefone"><input data-testid="gastro-phone-input" name="phone" value={form.phone} onChange={onChange} className="input-line" placeholder="+351 ..." /></Field>
              </div>
              <Field label="Mais informações">
                <textarea data-testid="gastro-message-input" name="message" value={form.message} onChange={onChange} rows={4} className="input-line resize-none" placeholder="Conte-nos sobre o evento (horário, convidados especiais, pedidos específicos)..." />
              </Field>
            </section>
          </div>
        </div>

        {date && (
          <p className="mt-10 text-sm text-muted-foreground capitalize text-center" data-testid="gastro-summary">
            {format(date, "EEEE, d 'de' MMMM yyyy", { locale: pt })} · {eventType || "Tipo de evento ?"} · {party} pessoas
          </p>
        )}

        <div className="mt-6 flex flex-col items-center">
          <button type="submit" disabled={loading} data-testid="gastro-submit-btn" className="w-full lg:w-auto group inline-flex items-center justify-center gap-3 h-14 px-10 rounded-full bg-terracotta text-cream font-medium text-[0.82rem] tracking-[0.2em] uppercase hover:brightness-95 transition-all disabled:opacity-60">
            {loading ? (<><Loader2 size={16} className="animate-spin" /> A enviar...</>) : (<>Enviar Pedido <ArrowRight size={16} strokeWidth={1.75} className="group-hover:translate-x-1 transition-transform" /></>)}
          </button>
          <p className="mt-3 text-center text-xs text-muted-foreground">Pedido sob consulta — respondemos com uma proposta personalizada.</p>
        </div>
      </form>

      <style>{`
        .input-line { width:100%; background:transparent; border:none; border-bottom:1px solid hsl(var(--border)); padding:10px 0; font-family:'Manrope',sans-serif; font-size:0.95rem; color:hsl(var(--foreground)); transition:border-color .2s ease; outline:none; }
        .input-line::placeholder { color:hsl(var(--muted-foreground) / 0.6); }
        .input-line:focus { border-bottom-color:hsl(var(--accent)); }
      `}</style>
    </div>
  );
}

function Label({ children }) {
  return <span className="block text-[0.72rem] tracking-[0.2em] uppercase text-muted-foreground mb-3 font-medium">{children}</span>;
}

function SectionTitle({ children, icon: Icon }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      {Icon && <Icon size={16} strokeWidth={1.5} className="text-terracotta" />}
      <Label>{children}</Label>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[0.7rem] tracking-[0.2em] uppercase text-muted-foreground mb-1">{label}</span>
      {children}
    </label>
  );
}

function ChoiceGroup({ label, value, onChange, options, testIdPrefix }) {
  return (
    <section>
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-group`}>
        {options.map((opt) => (
          <button
            type="button"
            key={opt}
            data-testid={`${testIdPrefix}-${opt}`}
            onClick={() => onChange(value === opt ? "" : opt)}
            className={`px-4 h-10 rounded-full text-sm border transition-colors ${value === opt ? "bg-terracotta text-cream border-terracotta" : "bg-transparent text-forest border-border hover:border-terracotta"}`}
          >
            {opt}
          </button>
        ))}
      </div>
    </section>
  );
}
